// PrivacyPolicy.jsx
import React from "react";

const PrivacyPolicy = () => {
  const lastUpdated = "August 12, 2025";

  return (
    <main className="min-h-screen bg-white text-gray-900 py-16 px-4 md:px-12 lg:px-24">
      <section className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="capitalize md:text-[45px] text-[33px]  font-bold mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500">Last updated: {lastUpdated}</p>
        </header>

        <article className="prose prose-lg dark:prose-invert">
          <p className="text-[18px] text-gray-500">
            Welcome to <strong>Town Bell</strong> ("we", "us", or "our"). This
            Privacy Policy explains how we collect, use, disclose, and protect
            personal information when you visit or use <em>townbell.in</em> (the
            “Site”). By using the Site, you agree to the terms described below.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            1. Information We Collect
          </h4>

          <h3 className="text-[20px] font-semibold text-black mt-4">
            1.1 Information you provide
          </h3>
          <p className="text-[18px] text-gray-500">
            We collect information you submit directly, such as name, email
            address, phone number, message content, property inquiry details,
            and any documents you upload (for example, contact forms, booking
            requests, or newsletters).
          </p>

          <h3 className="text-[20px] font-semibold text-black mt-4">
            1.2 Information collected automatically
          </h3>
          <p className="text-[18px] text-gray-500">
            When you visit the Site we may automatically collect technical data
            including IP address, browser type, operating system, referring
            URLs, pages visited, and device identifiers. We also use cookies and
            similar tracking technologies (see <em>Cookies &amp; Tracking</em>{" "}
            below).
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            2. How We Use Your Information
          </h4>
          <ul className="list-disc pl-6 text-[18px] text-gray-500">
            <li>To respond to inquiries, requests, and support messages.</li>
            <li>
              To provide, maintain, and improve the Site and our services.
            </li>
            <li>
              To send you marketing, newsletters, or promotional materials when
              you opt in.
            </li>
            <li>
              To detect, prevent, and address technical issues, fraud, security
              incidents, or legal claims.
            </li>
          </ul>

          <h4 className="text-[22px] font-bold text-black mt-6">
            3. Legal Basis for Processing (if applicable)
          </h4>
          <p className="text-[18px] text-gray-500">
            If you are in a jurisdiction that requires us to state a legal basis
            (for example the EU), we rely on your consent, contractual
            necessity, or our legitimate business interests (for security,
            analytics, and improvement of services).
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            4. Cookies &amp; Tracking Technologies
          </h4>
          <p className="text-[18px] text-gray-500">
            We use cookies, local storage, and similar technologies to operate
            the Site and collect analytics. You can control most cookies through
            your browser settings. Third-party services (e.g., Google Analytics)
            may set cookies and collect usage data — see their respective
            privacy policies for details.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            5. Sharing &amp; Disclosure
          </h4>
          <p className="text-[18px] text-gray-500">
            We do not sell personal information. We may share information with:
          </p>
          <ul className="list-disc pl-6 text-[18px] text-gray-500">
            <li>
              Service providers who help operate the Site (hosting, analytics,
              email delivery).
            </li>
            <li>
              Affiliates or partners when necessary to fulfill a service or
              request you made.
            </li>
            <li>
              When required by law, to comply with legal process, or to protect
              rights, safety, or property.
            </li>
          </ul>

          <h4 className="text-[22px] font-bold text-black mt-6">
            6. Data Retention
          </h4>
          <p className="text-[18px] text-gray-500">
            We retain personal information for as long as necessary to provide
            services, comply with legal obligations, resolve disputes, and
            enforce agreements. Retention periods depend on the type of
            information and purpose.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            7. Your Rights
          </h4>
          <p className="text-[18px] text-gray-500">
            Depending on your jurisdiction you may have rights to access,
            correct, delete, or port your personal data, or to object to certain
            processing. To exercise those rights, contact us using the details
            below.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">8. Security</h4>
          <p className="text-[18px] text-gray-500">
            We implement reasonable administrative, physical and technical
            safeguards to protect personal information. However, no method of
            transmission or storage is 100% secure — absolute security cannot be
            guaranteed.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            9. Links to Other Sites
          </h4>
          <p className="text-[18px] text-gray-500">
            The Site may contain links to third-party websites. We are not
            responsible for the privacy practices or content of those sites.
            Please review their privacy policies when you visit them.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            10. Children
          </h4>
          <p className="text-[18px] text-gray-500">
            The Site is not intended for children under 13 (or the relevant
            minimum age in your jurisdiction). We do not knowingly collect
            personal information from children. If a parent becomes aware that a
            child has provided us with personal information, please contact us
            to request deletion.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            11. Changes to This Policy
          </h4>
          <p className="text-[18px] text-gray-500">
            We may update this Privacy Policy from time to time. We will post
            the updated policy on the Site with the updated “Last updated” date.
            Significant changes will be communicated where required by law.
          </p>

          <h4 className="text-[22px] font-bold text-black mt-6">
            12. Contact Us
          </h4>
          <p className="text-[18px] text-gray-500">
            If you have questions, requests, or complaints about this policy,
            contact:
          </p>
          <address className="mt-4 ">
            <strong>Town Bell</strong>
            <br />
            Email:{" "}
            <a href="mailto:privacy@townbell.in" className="text-blue-600">
              privacy@townbell.in
            </a>
            <br />
            Website:{" "}
            <a href="https://townbell.in" className="text-blue-600">
              www.townbell.in
            </a>
          </address>

          
        </article>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
